import { MediaFileInfo, Menu, MenuDetails, MenuEditModel } from '@app/models'
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { IMediaFileInfo, IMenu, IMenuDetails, IMenuEditModel, IMenuProfile, IMenuType, ITag, RootState } from 'AppTypes'
import { api } from '../../../services'

export const initialState = {
  menuTypes: [] as IMenuType[],
  menuList: [] as IMenu[],
  activeTab: 1,
  editMode: 'create' as 'edit' | 'create',
  editedMenu: new MenuEditModel(),
  uploadVideoFile: null as File | null,
  uploadVideoUrl: '' as string | null,
  uploadImageFiles: [] as File[],
  uploadImageUrls: [] as string[],
  deletedImages: [] as string[],
  tags: [] as string[],
  allergens: [] as string[],
  tagSuggestions: [] as ITag[],
  allergenSuggestions: [] as string[],
  isPublic: true,
  isLoading: false,
  pageIndex: 1,
  pageSize: 9999
}

export const fetchAllergenSuggestions = createAsyncThunk(
  'menu/fetchAllergenSuggestions',
  async (keyword: string, { getState }) => {
    const state = getState() as RootState
    const response = await api.allergen.fetchAllergens({
      keyword,
      storeId: state.app.currentStore.id,
      pageIndex: 1,
      PageSize: 10
    })

    return response.data
  }
)

export const fetchTagSuggestions = createAsyncThunk(
  'menu/fetchTagSuggestions',
  async (keyword: string, { getState }) => {
    const state = getState() as RootState
    const response = await api.tag.fetchTags({
      keyword,
      storeId: state.app.currentStore.id,
      pageIndex: 1,
      PageSize: 10
    })

    return response.data
  }
)

export const fetchMenuTypes = createAsyncThunk(
  'menu/fetchMenuTypes',
  async (keyword: string, { getState }) => {
    const state = getState() as RootState
    const response = await api.menu.fetchMenuTypes({
      keyword,
      storeId: state.app.currentStore.id,
      pageIndex: 1,
      pageSize: 20
    })
    return response.data
  }
)

export const fetchMenuProfile = createAsyncThunk(
  'menu/fetchMenuProfile',
  async (_, { getState }) => {
    const state = getState() as RootState
    const response = await api.menu.fetchMenuProfile({
      storeId: state.app.currentStore.id,
      isPublish: state.menu.isPublic,
      pageIndex: state.menu.pageIndex,
      pageSize: state.menu.pageSize
    })
    return response.data
  }
)

export const changeMenuProfileStatus = createAsyncThunk<boolean, boolean>(
  'menu/changeMenuProfileStatus',
  async (isPublic: boolean) => {
    const response = await api.menu.changeMenuProfileStatus(isPublic)
    return response
  }
)

export const fetchMenuById = createAsyncThunk(
  'menu/fetchMenuById',
  async (id: string) => {
    const response = await api.menu.fetchMenuById(id) as IMenuDetails
    return response
  }
)

export const createMenu = createAsyncThunk(
  'menu/createMenu',
  async (item: IMenuEditModel, { getState }) => {
    const state = getState() as RootState

    const postItem = {
      ...state.menu.editedMenu,
      storeId: state.app.currentStore.id,
      menuName: item.menuName,
      price: item.price,
      description: item.description,
      isPublish: item.isPublish,
      menuTypeId: item.menuTypeId,
      tags: state.menu.tags,
      allergens: state.menu.allergens
    }

    // process video
    if (state.menu.uploadVideoFile) {
      const presignedInfo = await api.file.getPresignUrl({ dataType: 'Video', fileName: state.menu.uploadVideoFile.name, shopName: state.app.currentStore.storeName })
      const uploadResult = await api.file.uploadFileS3({
        url: presignedInfo.presignedUrl,
        // data: new FileReader().readAsArrayBuffer(state.menu.uploadVideoFile),
        data: state.menu.uploadVideoFile,
        fileType: state.menu.uploadVideoFile.type
      })

      postItem.video = new MediaFileInfo('', state.menu.uploadVideoFile.name, state.menu.uploadVideoFile.size, presignedInfo.key)
    }

    if (state.menu.uploadImageFiles) {
      /* eslint-disable-next-line no-restricted-syntax */
      for (const file of state.menu.uploadImageFiles) {
        const presignedInfo = await api.file.getPresignUrl({ dataType: 'Image', fileName: file.name, shopName: state.app.currentStore.storeName })
        const uploadResult = await api.file.uploadFileS3({
          url: presignedInfo.presignedUrl,
          // data: new FileReader().readAsArrayBuffer(state.menu.uploadVideoFile),
          data: file,
          fileType: file.type
        })

        postItem.images.push(new MediaFileInfo('', file.name, file.size, presignedInfo.key))
      }
    }

    await api.menu.createMenu(postItem)
  }
)

export const updateMenu = createAsyncThunk(
  'menu/updateMenu',
  async (item: IMenuEditModel) => {
    await api.menu.updateMenu(item)
  }
)

const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    changePublicFilter: (state, action: PayloadAction<boolean>) => {
      state.isPublic = action.payload
    },
    changeImages: (state, action: PayloadAction<File[]>) => {
      state.uploadImageFiles = state.uploadImageFiles.concat(action.payload)
      state.uploadImageUrls = state.uploadImageUrls.concat(action.payload.map(file => URL.createObjectURL(file)))
    },
    removeImage: (state, action: PayloadAction<string>) => {
      const index = state.uploadImageUrls.indexOf(action.payload)
      state.uploadImageUrls.splice(index, 1)
      state.uploadImageFiles.splice(index, 1)
    },
    removeVideo: state => {
      state.uploadVideoFile = null
      state.uploadVideoUrl = null
    },
    changeVideo: (state, action: PayloadAction<File>) => {
      state.uploadVideoFile = action.payload
      state.uploadVideoUrl = URL.createObjectURL(action.payload)
    },
    changeMenuTab: (state, action: PayloadAction<number>) => {
      state.activeTab = action.payload
    },
    beginCreateNewMenu: state => {
      state.activeTab = 2
      state.editMode = 'create'
      state.editedMenu = new MenuEditModel()
    },
    selectMenu: (state, action: PayloadAction<IMenu>) => {
      state.activeTab = 2
      state.editMode = 'edit'
      state.editedMenu = new MenuEditModel()
      state.editedMenu.menuName = action.payload.menuName
      state.editedMenu.description = action.payload.description
      state.editedMenu.price = action.payload.price
    },
    addTag: (state, action: PayloadAction<string>) => {
      if (state.tags.indexOf(action.payload) > -1) {
        return
      }

      state.tags.push(action.payload)
      state.tags = state.tags.slice()
      state.tagSuggestions = []
    },
    removeTag: (state, action: PayloadAction<string>) => {
      state.tags = state.tags.filter(t => t !== action.payload)
      state.tags = state.tags.slice()
    },
    addAllergen: (state, action: PayloadAction<string>) => {
      if (state.allergens.indexOf(action.payload) > -1) {
        return
      }

      state.allergens.push(action.payload)
      state.allergens = state.allergens.slice()
      state.allergenSuggestions = []
    },
    removeAllergen: (state, action: PayloadAction<string>) => {
      state.allergens = state.allergens.filter(a => a !== action.payload)
      state.allergens = state.allergens.slice()
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchAllergenSuggestions.fulfilled, (state, action) => {
      state.allergenSuggestions = action.payload
    })
    builder.addCase(fetchTagSuggestions.fulfilled, (state, action) => {
      state.tagSuggestions = action.payload
    })
    builder.addCase(changeMenuProfileStatus.fulfilled, (state, action) => {
      state.isPublic = action.payload
    })
    builder.addCase(fetchMenuProfile.fulfilled, (state, action) => {
      state.menuList = action.payload
      // state.isPublic = action.payload.isPublic
    })
    builder.addCase(fetchMenuTypes.fulfilled, (state, action) => {
      state.menuTypes = action.payload
    })
    builder.addCase(fetchMenuById.fulfilled, (state, action) => {
      state.editedMenu = new MenuEditModel()
      state.editedMenu.menuName = action.payload.menuName
      state.editedMenu.price = action.payload.price
      state.editedMenu.description = action.payload.description
      state.editedMenu.isPublish = action.payload.isPublish
      if (action.payload.menuType) {
        state.editedMenu.menuTypeId = action.payload.menuType.id
      }
      state.tags = action.payload.tags.map(tag => tag.tagName)
      state.allergens = action.payload.allergens
      state.activeTab = 2
      state.editMode = 'edit'
      if (action.payload.video) {
        state.uploadVideoUrl = action.payload.video.path
      }
      if (action.payload.images) {
        state.uploadImageUrls = action.payload.images.map(i => i.path)
      }
    })
    builder.addCase(createMenu.fulfilled, state => {
      state.isLoading = false
      state.editMode = 'create'
      state.editedMenu = new MenuEditModel()
      state.tags = []
      state.allergens = []
      state.tagSuggestions = []
      state.allergenSuggestions = []
      state.uploadImageFiles = []
      state.uploadImageUrls = []
      state.uploadVideoFile = null
      state.uploadVideoUrl = null
      state.activeTab = 1
    })
    builder.addCase(createMenu.rejected, state => {
      state.isLoading = false
    })
  }
})

export const {
  changeMenuTab,
  beginCreateNewMenu,
  selectMenu,
  addTag,
  removeTag,
  addAllergen,
  removeAllergen,
  changeVideo,
  removeVideo,
  changeImages,
  removeImage,
  changePublicFilter,
  setLoading
} = menuSlice.actions

export default menuSlice.reducer
